import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { canonicalUrl as buildCanonical, getStaticMeta } from "@/lib/seoShared";

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: "website" | "product" | "article";
  noindex?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  /** Set by <AutoSEO /> — the route-agnostic fallback instance. */
  isFallback?: boolean;
  /** Explicit robots value (e.g. "noindex, follow"); keeps static title/description. */
  robots?: string;
  /** Do not emit canonical/og:url (404 pages). */
  noCanonical?: boolean;
  /** Page language (default ru) → <html lang>, og:locale. */
  lang?: string;
  /** Language versions of this page → <link rel="alternate" hreflang>. Paths or absolute URLs. */
  alternates?: { lang: string; url: string }[];
}

const OG_LOCALE: Record<string, string> = { ru: "ru_RU", en: "en_US", es: "es_MX", fr: "fr_FR", de: "de_DE", th: "th_TH" };
const LANG_NAME: Record<string, string> = { ru: "Russian", en: "English", es: "Spanish", fr: "French", de: "German", th: "Thai" };

export const SEO = ({
  title,
  description,
  keywords,
  image = "https://api.biopeptideplus.ru/storage/v1/object/public/assets/og-image.jpg",
  url,
  type = "website",
  noindex = false,
  breadcrumbs,
  isFallback = false,
  robots,
  noCanonical = false,
  lang = "ru",
  alternates,
}: SEOProps) => {
  const location = useLocation();
  void breadcrumbs;
  void isFallback;
  const siteName = "BioPeptidePlus";
  // Static routes: always the same title/description as the prerendered HTML.
  const staticMeta = noindex ? null : getStaticMeta(location.pathname);
  if (staticMeta) {
    title = staticMeta.title;
    description = staticMeta.description;
    if (!robots && staticMeta.noindex) robots = "noindex, follow";
  }
  // Journal defaults to noindex until the loaded article supplies its v2 policy.
  if (!robots && /^\/journal\/[^/]+/.test(location.pathname)) robots = "noindex, follow";
  const robotsContent = robots || (noindex ? "noindex, nofollow" : "index, follow");
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
  // Production domain, no trailing slash.
  const canonicalUrl = url
    ? (url.startsWith("http") ? url.replace(/\/+$/, "") : buildCanonical(url))
    : buildCanonical(location.pathname);

  // index.html / prerendered HTML ships a static robots tag; Helmet's own tag
  // must be the only one, otherwise "index" and "noindex" coexist.
  useEffect(() => {
    document.querySelectorAll('meta[name="robots"]:not([data-rh])').forEach((m) => m.remove());
  }, []);

  return (
    <Helmet htmlAttributes={{ lang }}>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {!noCanonical && <link rel="canonical" href={canonicalUrl} />}
      <meta name="robots" content={robotsContent} />

      {/* Open Graph / Facebook / Telegram */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      {!noCanonical && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:locale" content={OG_LOCALE[lang] || "ru_RU"} />
      {alternates?.map((a) => (
        <link key={a.lang} rel="alternate" hrefLang={a.lang} href={a.url.startsWith("http") ? a.url : buildCanonical(a.url)} />
      ))}
      {alternates?.length ? (
        <link rel="alternate" hrefLang="x-default" href={((u) => (u.startsWith("http") ? u : buildCanonical(u)))(alternates.find((a) => a.lang === "ru")?.url || alternates[0].url)} />
      ) : null}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO */}
      <meta name="author" content={siteName} />
      <meta name="geo.region" content="RU" />
      <meta name="geo.placename" content="Russia" />
      <meta name="language" content={LANG_NAME[lang] || "Russian"} />

      {/* Breadcrumb JSON-LD is emitted once by the static prerender. */}
    </Helmet>
  );
};
